const sqlCheatsheet = {
  id: "sql",
  title: "SQL Cheatsheet",
  description:
    "A comprehensive guide to SQL including database management, DDL, DML, queries, joins, functions, and best practices",
  language: "sql",
  category: "Database",
  level: "All Levels",
  sections: [
    {
      title: "Database Management",
      items: [
        {
          title: "Database Creation and Management",
          content: "Basic database operations",
          code: `-- Create new database
CREATE DATABASE myDatabase;

-- Create database with specific charset and collation
CREATE DATABASE myDatabase
CHARACTER SET utf8mb4
COLLATE utf8mb4_unicode_ci;

-- Show all databases
SHOW DATABASES;

-- Select/Use database
USE myDatabase;

-- Show current database
SELECT DATABASE();

-- Show database creation statement
SHOW CREATE DATABASE myDatabase;

-- Drop/Delete database
DROP DATABASE myDatabase;

-- Drop only if exists
DROP DATABASE IF EXISTS myDatabase;`,
          note: "Basic database creation and management commands",
        },
        {
          title: "Database Administration",
          content: "Database maintenance and administration",
          code: `-- Show database status
SHOW STATUS;

-- Show database variables
SHOW VARIABLES;

-- Show database processes
SHOW PROCESSLIST;

-- Show database size
SELECT 
    table_schema AS "Database",
    ROUND(SUM(data_length + index_length) / 1024 / 1024, 2) AS "Size (MB)"
FROM information_schema.TABLES
GROUP BY table_schema;

-- Database backup (MySQL)
mysqldump -u username -p myDatabase > backup.sql

-- Database restore (MySQL)
mysql -u username -p myDatabase < backup.sql

-- Grant privileges
GRANT ALL PRIVILEGES ON myDatabase.*
TO 'username'@'localhost'
WITH GRANT OPTION;

-- Show grants
SHOW GRANTS FOR 'username'@'localhost';`,
          note: "Database administration and maintenance tasks",
        },
      ],
    },
    {
      title: "Table Operations (DDL)",
      items: [
        {
          title: "Create Table",
          content: "Table creation with constraints",
          code: `-- Basic table creation
CREATE TABLE employees (
    id INT PRIMARY KEY AUTO_INCREMENT,
    first_name VARCHAR(50) NOT NULL,
    last_name VARCHAR(50) NOT NULL,
    email VARCHAR(100) UNIQUE,
    hire_date DATE DEFAULT CURRENT_DATE,
    salary DECIMAL(10,2),
    department_id INT,
    FOREIGN KEY (department_id) 
        REFERENCES departments(id)
);

-- Create table with additional constraints
CREATE TABLE products (
    product_id INT PRIMARY KEY,
    name VARCHAR(100) NOT NULL,
    price DECIMAL(10,2) CHECK (price > 0),
    stock INT DEFAULT 0,
    category VARCHAR(50),
    CONSTRAINT unique_name UNIQUE (name)
);

-- Create table from query
CREATE TABLE employee_archive 
AS SELECT * FROM employees 
WHERE hire_date < '2020-01-01';`,
          note: "Creating tables with various constraints and options",
        },
        {
          title: "Modify Table",
          content: "Alter table structure",
          code: `-- Add column
ALTER TABLE employees 
ADD COLUMN phone VARCHAR(15);

-- Modify column
ALTER TABLE employees
MODIFY COLUMN phone VARCHAR(20);

-- Drop column
ALTER TABLE employees
DROP COLUMN phone;

-- Add constraint
ALTER TABLE employees
ADD CONSTRAINT fk_dept
FOREIGN KEY (department_id) 
REFERENCES departments(id);

-- Drop constraint
ALTER TABLE employees
DROP CONSTRAINT fk_dept;

-- Rename table
ALTER TABLE employees
RENAME TO staff;

-- Show table structure
DESCRIBE employees;
SHOW CREATE TABLE employees;`,
          note: "Modifying existing table structures",
        },
      ],
    },
    {
      title: "Data Manipulation (DML)",
      items: [
        {
          title: "Insert Data",
          content: "Adding records to tables",
          code: `-- Insert single row
INSERT INTO employees (first_name, last_name, email)
VALUES ('John', 'Doe', 'john@example.com');

-- Insert multiple rows
INSERT INTO employees 
    (first_name, last_name, email)
VALUES 
    ('Jane', 'Smith', 'jane@example.com'),
    ('Bob', 'Johnson', 'bob@example.com');

-- Insert with select
INSERT INTO employee_archive
SELECT * FROM employees
WHERE hire_date < '2020-01-01';`,
          note: "Different ways to insert data into tables",
        },
        {
          title: "Update Data",
          content: "Modifying existing records",
          code: `-- Update single row
UPDATE employees
SET salary = 60000
WHERE id = 1;

-- Update multiple columns
UPDATE employees
SET 
    salary = salary * 1.1,
    last_modified = CURRENT_TIMESTAMP
WHERE department_id = 2;

-- Update with subquery
UPDATE products
SET price = price * 1.1
WHERE category IN (
    SELECT category 
    FROM product_categories 
    WHERE tax_rate > 0.2
);`,
          note: "Different ways to update existing data",
        },
        {
          title: "Delete Data",
          content: "Removing records from tables",
          code: `-- Delete specific records
DELETE FROM employees
WHERE department_id = 3;

-- Delete with subquery
DELETE FROM products
WHERE product_id IN (
    SELECT product_id 
    FROM order_items 
    GROUP BY product_id 
    HAVING SUM(quantity) = 0
);

-- Delete all records
TRUNCATE TABLE employee_archive;`,
          note: "Different ways to delete data from tables",
        },
      ],
    },
    {
      title: "Querying Data (DQL)",
      items: [
        {
          title: "Basic Queries",
          content: "Essential SELECT statements",
          code: `-- Select all columns
SELECT * FROM employees;

-- Select specific columns
SELECT first_name, last_name, salary 
FROM employees;

-- Select with conditions
SELECT * FROM employees
WHERE salary > 50000
AND department_id = 2;

-- Select with pattern matching
SELECT * FROM employees
WHERE last_name LIKE 'S%'
OR email LIKE '%@gmail.com';

-- Select distinct values
SELECT DISTINCT department_id
FROM employees;

-- Select with calculated fields
SELECT 
    first_name,
    salary,
    salary * 0.1 AS bonus,
    salary * 1.1 AS new_salary
FROM employees;`,
          note: "Basic query operations for retrieving data",
        },
        {
          title: "Joins",
          content: "Combining data from multiple tables",
          code: `-- Inner join
SELECT 
    e.first_name,
    e.last_name,
    d.department_name
FROM employees e
INNER JOIN departments d
ON e.department_id = d.id;

-- Left join
SELECT 
    e.first_name,
    e.last_name,
    d.department_name
FROM employees e
LEFT JOIN departments d
ON e.department_id = d.id;

-- Multiple joins
SELECT 
    e.first_name,
    d.department_name,
    l.city
FROM employees e
JOIN departments d ON e.department_id = d.id
JOIN locations l ON d.location_id = l.id;

-- Self join
SELECT 
    e1.first_name as employee,
    e2.first_name as manager
FROM employees e1
LEFT JOIN employees e2
ON e1.manager_id = e2.id;`,
          note: "Different types of joins for combining data",
        },
      ],
    },
    {
      title: "Advanced Features",
      items: [
        {
          title: "Transactions",
          content: "Transaction management",
          code: `-- Start transaction
START TRANSACTION;

-- Multiple operations
INSERT INTO orders (customer_id, total)
VALUES (1, 100.00);

UPDATE inventory
SET quantity = quantity - 1
WHERE product_id = 123;

-- Commit if successful
COMMIT;

-- Rollback if error
ROLLBACK;

-- Set auto-commit
SET autocommit = 0;  -- Disable
SET autocommit = 1;  -- Enable`,
          note: "Managing database transactions",
        },
        {
          title: "Views",
          content: "Creating and managing views",
          code: `-- Create view
CREATE VIEW employee_details AS
SELECT 
    e.first_name,
    e.last_name,
    d.department_name,
    e.salary
FROM employees e
JOIN departments d ON e.department_id = d.id;

-- Create materialized view (PostgreSQL)
CREATE MATERIALIZED VIEW dept_summary AS
SELECT 
    department_id,
    COUNT(*) as emp_count,
    AVG(salary) as avg_salary
FROM employees
GROUP BY department_id;

-- Update view
ALTER VIEW employee_details AS
SELECT 
    e.first_name,
    e.last_name,
    d.department_name,
    e.salary,
    e.hire_date
FROM employees e
JOIN departments d ON e.department_id = d.id;`,
          note: "Creating and managing database views",
        },
      ],
    },
  ],
};

export default sqlCheatsheet;
